import React, { useMemo } from "react";
import type { BatchCase, BatchPhase } from "../../hooks/useBatchUpload";
import { FaCheck, FaExclamationTriangle, FaSpinner, FaUpload } from "../../utils/icons";
import { batchMonitorSummary, batchProgressCounts } from "./batchUploadSummary";

interface BatchUploadSidebarRowProps {
  phase: BatchPhase;
  cases: BatchCase[];
  active: boolean;
  onSelect: () => void;
}

/** Session-retained batch entry shown above individual uploads. */
export const BatchUploadSidebarRow: React.FC<BatchUploadSidebarRowProps> = ({
  phase,
  cases,
  active,
  onSelect,
}) => {
  const summary = useMemo(() => batchMonitorSummary(phase, cases), [cases, phase]);
  const counts = useMemo(() => batchProgressCounts(cases), [cases]);
  const doneWithFailures =
    phase === "done" &&
    cases.some((item) => item.status === "failed" || item.status === "upload-failed");
  const inProgress = phase === "uploading" || phase === "analyzing" || phase === "parsing";

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? "page" : undefined}
      data-testid="batch-upload-sidebar-row"
      className={`group w-full border-b border-l-2 border-border px-4 py-3 text-left transition-colors hover:bg-surface-2 ${
        active ? "border-l-signal bg-signal-tint" : "border-l-transparent"
      }`}
    >
      <span className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 font-mono text-[9px] font-bold uppercase tracking-widest text-signal-text-tint">
          {doneWithFailures ? (
            <span className="text-warn">
              <FaExclamationTriangle size={10} />
            </span>
          ) : phase === "done" ? (
            <FaCheck size={10} />
          ) : inProgress ? (
            <FaSpinner className="animate-spin" size={10} />
          ) : (
            <FaUpload size={10} />
          )}
          Batch upload
        </span>
        <span className="font-mono text-[10px] tabular-nums text-fg-muted">{summary.percent}%</span>
      </span>
      <span className="mt-1 block text-sm font-medium text-fg">{summary.label}</span>
      <span className="mt-0.5 block text-xs text-fg-muted">
        {counts.uploaded}/{counts.total} uploaded · {counts.processed}/{counts.total} processed
        {counts.errors > 0 && (
          <span className="text-warn">
            {" "}
            · {counts.errors} {counts.errors === 1 ? "error" : "errors"}
          </span>
        )}
      </span>
      <span className="mt-2 block h-1 overflow-hidden rounded bg-surface-2" aria-hidden="true">
        <span
          data-testid="batch-sidebar-progress"
          className="block h-full bg-signal transition-[width] duration-300"
          style={{ width: `${summary.percent}%` }}
        />
      </span>
    </button>
  );
};
